








































































































































































import {
  Component,
  Mixins,
  Prop,
  Emit,
  Watch,
  Ref
} from "vue-property-decorator";
import { scanImageData } from "@undecaf/zbar-wasm";
import AxiosMixin from "@/mixins/axiosMixin";
import { ScannedMedicine } from "@/components/patient/medicine/types";

interface QRPageInfo {
  dataId: string;
  totalSplits: number;
  sequenceNumber: number;
  hasSplitInfo: boolean;
}

interface ScannedSummary {
  patientName: string;
  dates: string[];
  medicineNames: string[];
}

type ScanState = "scanning" | "confirming";

@Component
export default class QRCodeScannerDialog extends Mixins(AxiosMixin) {
  @Prop({ default: false }) value!: boolean;

  @Ref("video") private videoEl!: HTMLVideoElement;
  @Ref("fileInput") private fileInputEl!: HTMLInputElement;

  private stream: MediaStream | null = null;
  private scanIntervalId: number | null = null;
  private scanCanvas: HTMLCanvasElement | null = null;
  private scanning = false;
  private imageCapture: unknown = null;
  private useTakePhoto = false;

  private scanState: ScanState = "scanning";
  private cameraError = "";
  private imageDecoding = false;
  private imageDecodeError = "";
  private imageDecodeSuccess = "";

  /**
   * スキャン済みQRコード。
   * 911ありの場合: キー = データ連番（正数）
   * 911なし / SA続きの場合: キー = -1, -2, -3...（スキャン順の負数）
   */
  private scannedPages: Map<number, string> = new Map();


  private get isOpen(): boolean {
    return this.value;
  }

  private set isOpen(v: boolean) {
    this.input(v);
  }

  @Emit("input")
  private input(v: boolean): boolean {
    return v;
  }

  @Emit("scanned")
  private scanned(items: ScannedMedicine[]): ScannedMedicine[] {
    return items;
  }

  @Watch("value")
  private onValueChange(val: boolean) {
    if (val) {
      this.scannedPages = new Map();
      this.scanState = "scanning";
      this.imageDecodeError = "";
      this.imageDecodeSuccess = "";
      this.$nextTick(() => this.startCamera());
    } else {
      this.stopCamera();
    }
  }

  private async startCamera() {
    this.cameraError = "";
    this.imageCapture = null;
    this.useTakePhoto = false;
    try {
      // JAHIS QRコードはバージョン40クラスの高密度になりやすいため4Kを要求。
      // frameRateを下げて4K取得の成功率を上げる（取れない端末は自動的に下位解像度に落ちる）。
      this.stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: "environment" },
          width: { ideal: 3840 },
          height: { ideal: 2160 },
          frameRate: { ideal: 15 }
        }
      });
      this.videoEl.srcObject = this.stream;
      await this.videoEl.play();

      const track = this.stream.getVideoTracks()[0];

      // 細かいQRコードはピントが命なので連続オートフォーカスを要求する
      try {
        const caps = (track.getCapabilities?.() ?? {}) as {
          focusMode?: string[];
        };
        if (caps.focusMode && caps.focusMode.includes("continuous")) {
          await track.applyConstraints({
            advanced: [{ focusMode: "continuous" } as MediaTrackConstraintSet]
          });
        }
      } catch {
        // 制約が一部でも未対応なら無視（Safari等）
      }

      // ImageCaptureが使えて、かつ写真解像度が動画解像度より十分大きい場合のみtakePhotoを使う。
      // Safari/iOSは未対応のためビデオフレーム経由に自動フォールバック。
      try {
        const W = (window as unknown) as {
          ImageCapture?: new (track: MediaStreamTrack) => {
            takePhoto: () => Promise<Blob>;
            getPhotoCapabilities: () => Promise<{
              imageWidth?: { max?: number };
            }>;
          };
        };
        if (W.ImageCapture) {
          const ic = new W.ImageCapture(track);
          const photoCaps = await ic.getPhotoCapabilities();
          const settings = track.getSettings();
          const videoW = settings.width ?? 0;
          const photoMaxW = photoCaps?.imageWidth?.max ?? 0;
          this.imageCapture = ic;
          this.useTakePhoto = photoMaxW > videoW * 1.2;
        }
      } catch {
        this.imageCapture = null;
        this.useTakePhoto = false;
      }

      this.scanCanvas = document.createElement("canvas");
      this.scanIntervalId = window.setInterval(
        () => this.scanCameraFrame(),
        this.useTakePhoto ? 700 : 400
      );
    } catch {
      this.cameraError =
        "カメラへのアクセスが許可されていません。ブラウザの設定を確認してください。";
    }
  }

  private async scanCameraFrame() {
    if (this.scanning || !this.scanCanvas) return;

    this.scanning = true;
    try {
      const ctx = this.scanCanvas.getContext("2d");
      if (!ctx) return;

      let width = 0;
      let height = 0;
      let drawn = false;

      // 写真解像度が利用できればtakePhotoでセンサーフル解像度を取得する
      if (this.useTakePhoto && this.imageCapture) {
        try {
          const ic = this.imageCapture as { takePhoto: () => Promise<Blob> };
          const blob = await ic.takePhoto();
          const bitmap = await createImageBitmap(blob);
          width = bitmap.width;
          height = bitmap.height;
          this.scanCanvas.width = width;
          this.scanCanvas.height = height;
          ctx.drawImage(bitmap, 0, 0);
          bitmap.close();
          drawn = true;
        } catch {
          // takePhotoが落ちた端末は以降ビデオフレームに切り替える
          this.useTakePhoto = false;
        }
      }

      if (!drawn) {
        if (!this.videoEl || this.videoEl.readyState < 2) return;
        width = this.videoEl.videoWidth;
        height = this.videoEl.videoHeight;
        if (!width || !height) return;
        this.scanCanvas.width = width;
        this.scanCanvas.height = height;
        ctx.drawImage(this.videoEl, 0, 0);
      }

      // 中央85%の正方形だけをデコード対象にする（モジュール解像度向上＋処理量削減）
      const roiSize = Math.floor(Math.min(width, height) * 0.85);
      const roiX = Math.floor((width - roiSize) / 2);
      const roiY = Math.floor((height - roiSize) / 2);
      const imageData = ctx.getImageData(roiX, roiY, roiSize, roiSize);
      const symbols = await scanImageData(imageData);
      for (const symbol of symbols) {
        let text: string;
        try {
          text = symbol.decode("Shift_JIS");
        } catch {
          text = symbol.decode();
        }
        this.registerSymbol(text);
      }
      this.checkAutoComplete();
    } catch {
      // WASMの初期化前などに起きるエラーは無視
    } finally {
      this.scanning = false;
    }
  }

  private stopCamera() {
    if (this.scanIntervalId !== null) {
      clearInterval(this.scanIntervalId);
      this.scanIntervalId = null;
    }
    if (this.stream) {
      this.stream.getTracks().forEach(t => t.stop());
      this.stream = null;
    }
    this.scanCanvas = null;
    this.scanning = false;
    this.imageCapture = null;
    this.useTakePhoto = false;
  }

  private triggerFileInput() {
    this.imageDecodeError = "";
    this.imageDecodeSuccess = "";
    this.fileInputEl.value = "";
    this.fileInputEl.click();
  }

  private async onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    this.imageDecodeError = "";
    this.imageDecodeSuccess = "";
    this.imageDecoding = true;
    try {
      const bitmap = await createImageBitmap(file);
      const canvas = document.createElement("canvas");
      canvas.width = bitmap.width;
      canvas.height = bitmap.height;
      const ctx = canvas.getContext("2d");
      if (!ctx) throw new Error("Canvas2D unavailable");
      ctx.drawImage(bitmap, 0, 0);
      bitmap.close();
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const symbols = await scanImageData(imageData);

      if (symbols.length === 0) {
        this.imageDecodeError =
          "QRコードを検出できませんでした。鮮明な写真で再試行してください";
        return;
      }

      let addedCount = 0;
      let skippedCount = 0;
      let nonJahisCount = 0;

      for (const symbol of symbols) {
        let text: string;
        try {
          text = symbol.decode("Shift_JIS");
        } catch {
          text = symbol.decode();
        }

        const info = this.parseQRInfo(text);
        if (!info && !this.isJahisContinuation(text)) {
          nonJahisCount++;
          continue;
        }

        const sizeBefore = this.scannedPages.size;
        this.registerSymbol(text);
        if (this.scannedPages.size > sizeBefore) {
          addedCount++;
        } else {
          skippedCount++;
        }
      }

      if (addedCount === 0 && skippedCount === 0) {
        this.imageDecodeError =
          "JAHIS形式のQRコードを検出できませんでした。鮮明な写真で再試行してください";
      } else if (addedCount > 0) {
        const parts: string[] = [`${addedCount}枚読み込みました`];
        if (skippedCount > 0) parts.push(`${skippedCount}枚は読み込み済み`);
        this.imageDecodeSuccess = parts.join("、");
        this.checkAutoComplete();
      } else {
        this.imageDecodeSuccess = "すべてのQRコードはすでに読み込み済みです";
      }
    } catch (e) {
      this.imageDecodeError = `QR検出失敗: ${e}`;
    } finally {
      this.imageDecoding = false;
    }
  }

  /**
   * スキャン済みMapにシンボルを追加する（カメラ・写真共通）。
   * 自動完了の判定は呼び出し元が行う（複数シンボル処理後にまとめてチェック）。
   */
  private registerSymbol(text: string) {
    const info = this.parseQRInfo(text);

    if (!info) {
      // JAHISヘッダーなし → Structured Append連結QRコードの続きの可能性
      if (!this.isJahisContinuation(text)) return;
      const alreadyScanned = Array.from(this.scannedPages.values()).some(
        v => v === text
      );
      if (alreadyScanned) return;
      const nextKey = -(this.scannedPages.size + 1);
      this.scannedPages = new Map(this.scannedPages).set(nextKey, text);
      return;
    }

    if (info.hasSplitInfo) {
      // 911レコードあり: データ連番をキーに使って重複排除
      if (this.scannedPages.has(info.sequenceNumber)) return;
      this.scannedPages = new Map(this.scannedPages).set(
        info.sequenceNumber,
        text
      );
    } else {
      const alreadyScanned = Array.from(this.scannedPages.values()).some(
        v => v === text
      );
      if (alreadyScanned) return;
      const nextKey = -(this.scannedPages.size + 1);
      this.scannedPages = new Map(this.scannedPages).set(nextKey, text);
    }
  }

  /**
   * JAHISヘッダー（JAHISTC）を持たない連結QRコードの続きデータかどうかを判定する。
   * 典型的なJAHISレコード種別（1, 5, 201, 301等）が含まれている場合はtrueを返す。
   */
  private isJahisContinuation(text: string): boolean {
    const firstLine = text.split(/\r?\n/)[0].trim();
    if (firstLine.startsWith("JAHISTC")) return false;
    const jahisRecordPattern =
      /^(1|5|11|15|51|55|201|281|301|311|391|401|601|701|911),/;
    return text
      .split(/\r?\n/)
      .some(line => jahisRecordPattern.test(line.trim()));
  }

  /** SA枚数がライブラリから取得できないため自動完了は行わない。ユーザーが「確認」を押す。 */
  private checkAutoComplete() {
    // no-op
  }

  /** 1枚目のみスキャン済みで薬品データがない = SA連結QRコードの途中 */
  private get isAwaitingContinuation(): boolean {
    const s = this.summary;
    return s !== null && s.patientName !== "" && s.medicineNames.length === 0;
  }

  private get summary(): ScannedSummary | null {
    if (this.scannedPages.size === 0) return null;

    let patientName = "";
    const dates: string[] = [];
    const medicineNames: string[] = [];

    const positiveKeys = Array.from(this.scannedPages.keys())
      .filter(k => k > 0)
      .sort((a, b) => a - b);
    const negativeKeys = Array.from(this.scannedPages.keys())
      .filter(k => k < 0)
      .sort((a, b) => b - a); // 降順: -1 > -2 なのでスキャン順になる

    for (const key of [...positiveKeys, ...negativeKeys]) {
      const text = this.scannedPages.get(key) ?? "";
      for (const line of text.split(/\r?\n/)) {
        const cols = this.parseCsvLine(line.trim());
        if (cols.length === 0) continue;
        if (cols[0] === "1" && !patientName) {
          patientName = (cols[1] ?? "").trim();
        } else if (cols[0] === "5" && cols[1]) {
          const d = this.formatDate(cols[1]);
          if (d && !dates.includes(d)) dates.push(d);
        } else if (cols[0] === "201" && cols[2]) {
          const name = cols[2].trim();
          if (name && !medicineNames.includes(name)) medicineNames.push(name);
        }
      }
    }

    return { patientName, dates, medicineNames };
  }

  private showConfirmation() {
    this.stopCamera();
    this.scanState = "confirming";
  }

  private backToScanning() {
    this.scannedPages = new Map();
    this.scanState = "scanning";
    this.imageDecodeError = "";
    this.imageDecodeSuccess = "";
    this.$nextTick(() => this.startCamera());
  }

  /** スキャン済みデータを保持したままカメラに戻る（薬品が足りない場合の追加スキャン用） */
  private addMoreScan() {
    this.scanState = "scanning";
    this.imageDecodeError = "";
    this.imageDecodeSuccess = "";
    this.$nextTick(() => this.startCamera());
  }

  private complete() {
    const items = this.mergeAndParse();
    this.isOpen = false;
    this.scanned(items);
  }

  /**
   * 911レコードから分割情報を取得する。
   * 911,<データ固有ID>,<分割数>,<データ連番>
   * 911レコードがない場合は hasSplitInfo=false を返す。
   */
  private parseQRInfo(text: string): QRPageInfo | null {
    const firstLine = text.split(/\r?\n/)[0].trim();
    if (!firstLine.startsWith("JAHISTC")) return null;

    for (const line of text.split(/\r?\n/)) {
      const cols = this.parseCsvLine(line.trim());
      if (cols[0] !== "911" || cols.length < 4) continue;
      const totalSplits = parseInt(cols[2] ?? "1", 10);
      const sequenceNumber = parseInt(cols[3] ?? "1", 10);
      if (!isNaN(totalSplits) && !isNaN(sequenceNumber)) {
        return {
          dataId: cols[1] ?? "",
          totalSplits,
          sequenceNumber,
          hasSplitInfo: true
        };
      }
    }

    return { dataId: "", totalSplits: 1, sequenceNumber: 1, hasSplitInfo: false };
  }

  /** スキャン済みQRコードを順序通りに結合してパースする */
  private mergeAndParse(): ScannedMedicine[] {
    const positiveKeys = Array.from(this.scannedPages.keys())
      .filter(k => k > 0)
      .sort((a, b) => a - b);
    const negativeKeys = Array.from(this.scannedPages.keys())
      .filter(k => k < 0)
      .sort((a, b) => b - a); // 降順: -1 > -2 なのでスキャン順になる
    const sortedKeys = [...positiveKeys, ...negativeKeys];
    const allLines: string[] = [];
    for (const key of sortedKeys) {
      const text = this.scannedPages.get(key) ?? "";
      const lines = text
        .split(/\r?\n/)
        .map(l => l.trim())
        .filter(l => l.length > 0);
      allLines.push(...lines);
    }
    return this.parseJahisLines(allLines);
  }

  /**
   * JAHISお薬手帳 JAHISTC形式のCSV行をパースする。
   * JAHIS電子版お薬手帳データフォーマット仕様書 Ver.2.6 準拠。
   * 薬品コード種別: 1=なし, 2=レセプト電算, 3=厚労省, 4=YJコード, 6=HOT
   */
  private parseJahisLines(lines: string[]): ScannedMedicine[] {
    const results: ScannedMedicine[] = [];
    let prescriptionDate = "";
    let pending: Partial<ScannedMedicine> | null = null;

    const flush = () => {
      if (pending?.medicine_name) {
        results.push({
          yj_code: pending.yj_code ?? "",
          medicine_name: pending.medicine_name,
          start_date: pending.start_date ?? "",
          remarks: pending.remarks ?? ""
        });
      }
      pending = null;
    };

    for (const line of lines) {
      const cols = this.parseCsvLine(line);
      if (cols.length === 0) continue;

      const rowType = cols[0];

      if (rowType === "5") {
        prescriptionDate = this.formatDate(cols[1] ?? "");
      } else if (rowType === "201") {
        flush();
        const medicineName = cols[2] ?? "";
        if (!medicineName) continue;
        const capacity = [cols[3] ?? "", cols[4] ?? ""]
          .filter(Boolean)
          .join("");
        const codeType = cols[5] ?? "";
        const yjCode = codeType === "4" ? cols[6] ?? "" : "";
        pending = {
          yj_code: yjCode,
          medicine_name: medicineName,
          start_date: prescriptionDate,
          remarks: capacity
        };
      } else if (rowType === "301" && pending) {
        const timing = cols[2] ?? "";
        const dispensingQty = cols[3] ?? "";
        const dispensingUnit = cols[4] ?? "";
        const dispensingInfo = [dispensingQty, dispensingUnit]
          .filter(Boolean)
          .join("");
        const extra = [timing, dispensingInfo].filter(Boolean).join(" ");
        if (extra) {
          pending.remarks = [pending.remarks, extra].filter(Boolean).join(" ");
        }
      }
    }

    flush();
    return results;
  }

  /** CSVの1行をカンマ区切りで分割（ダブルクォート考慮） */
  private parseCsvLine(line: string): string[] {
    const result: string[] = [];
    let current = "";
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') {
        if (inQuotes && line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (ch === "," && !inQuotes) {
        result.push(current);
        current = "";
      } else {
        current += ch;
      }
    }
    result.push(current);
    return result;
  }

  /**
   * JAHIS日付フォーマットをYYYY-MM-DDに変換する。
   * 西暦8桁（YYYYMMDD）と和暦7桁（GYYMMDD）の両方に対応。
   * G: M=明治, T=大正, S=昭和, H=平成, R=令和
   */
  private formatDate(dateStr: string): string {
    if (dateStr.length === 8) {
      return `${dateStr.slice(0, 4)}-${dateStr.slice(4, 6)}-${dateStr.slice(
        6,
        8
      )}`;
    }
    if (dateStr.length === 7) {
      const eraOffsets: Record<string, number> = {
        M: 1867,
        T: 1911,
        S: 1925,
        H: 1988,
        R: 2018
      };
      const era = dateStr[0].toUpperCase();
      const offset = eraOffsets[era];
      if (offset === undefined) return "";
      const year = offset + parseInt(dateStr.slice(1, 3), 10);
      return `${year}-${dateStr.slice(3, 5)}-${dateStr.slice(5, 7)}`;
    }
    return "";
  }

  private close() {
    this.isOpen = false;
  }

  beforeDestroy() {
    this.stopCamera();
  }
}
